import { useEffect, useRef, useState } from 'react'
import { onEdgeEvent } from '../servers/commandStream'
import { useQuery } from '@tanstack/react-query'
import { FolderField } from '../servers/FolderField'
import { ApiError } from '../transport/http'
import {
  forkAcross,
  forkAcrossProgress,
  forkAgent,
  getAccountDevices,
  type AgentRow,
  type ForkProgress,
} from '../transport/api'
import {
  forkCrossOf,
  forkFailure,
  forkLegViews,
  forkPollDelay,
  forkStageLabel,
  forkTargets,
  type ForkFailure,
} from './forkAcross'

/**
 * Fork a conversation — onto this machine, or onto another one.
 *
 * Two backends behind one form, because to the person filling it in they are one
 * thing: a copy of this agent, somewhere, in a folder. What differs is the route:
 *
 *   same machine   /agent/fork      the edge's own --fork-session, sub-second
 *   another one    /account/agents/fork-across   the BFF orchestrating both ends
 *
 * The cross-machine route is the one with all the behaviour, and every piece of it is
 * somebody's hard-won rule:
 *
 * · IT DOES NOT GO THROUGH THE RELAY. `api.post` gives up after 35s; this call
 *   legitimately runs to 300s. See transport/relay.ts forkAcross.
 * · SUCCESS COMES FROM THE ANSWER, never from the roster. A forked agent can be alive
 *   and not yet announced — that cost two developers an afternoon on 2026-08-22, both
 *   reading an empty list as a dead agent. So this reports what the endpoint said and
 *   offers a button; it does NOT navigate on its own, and it does not wait for the
 *   agent to appear anywhere.
 * · THE WAIT IS NAMED, not spun. Four legs, each with its own clock, because a
 *   300-second ceiling makes "hung" and "slow" look identical (avg-latency). The
 *   progress endpoint is display only — it can say `unknown` without that meaning
 *   anything is wrong.
 * · RETRY IS PLAIN. The endpoint is single-flight on `fork_id` for 90s, so pressing it
 *   again joins the first attempt rather than starting a second fork. No double-click
 *   guard of our own (avg-latency: 不要自己再包一層防連點).
 */
export function ForkForm({
  agent,
  sourceDeviceId,
  machine,
  onBack,
  onForked,
}: {
  agent: AgentRow
  /** The machine this agent runs on — the fork's source, and the row we exclude. */
  sourceDeviceId: string
  /** Readable name for that machine. */
  machine: string
  onBack: () => void
  /** A fork landed HERE: open it, as the same-machine path always did. */
  onForked: (newName: string, host: string | null) => void
}) {
  const [name, setName] = useState(`${agent.session}-fork`)
  const [target, setTarget] = useState<string>('') // '' = this machine
  /*
   * STARTS EMPTY SO THE MACHINE FILLS IT IN — owner, 2026-08-22: 需要依照目前 create
   * agent 的 folder selection 方式來做. default 路徑是 home folder.
   *
   * It used to open on the SOURCE agent's folder, which looked like a helpful default
   * and was a different rule from the one create uses. FolderField asks the machine for
   * its home and writes the answer in; seeding a value here would suppress that, since
   * it only prefills a field nobody has filled. Empty is not a blank field — it is the
   * field a moment before the machine answers.
   */
  const [cwd, setCwd] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ForkFailure | null>(null)
  const [done, setDone] = useState<{ session: string; machine: string } | null>(null)
  const [progress, setProgress] = useState<ForkProgress | undefined>(undefined)

  const devQ = useQuery({ queryKey: ['menu-machines'], queryFn: getAccountDevices, staleTime: 30_000 })
  /*
   * The same cached `/account/devices` the picker already reads — no new request, and
   * the answer is the EDGE's (`fork_cross`), not a guess from the CLI name.
   */
  const forkCross = forkCrossOf(devQ.data?.devices, agent.hash)
  const targets = forkTargets(devQ.data?.devices, sourceDeviceId, forkCross)
  const targetRow = targets.find((t) => t.deviceId === target)
  const targetName = targetRow?.name ?? machine
  const across = target !== ''

  /*
   * One id for this fork, minted here and kept across retries. That is what makes the
   * retry button safe to press: the endpoint is single-flight on it, so a second POST
   * joins the first rather than forking twice.
   */
  const forkId = useRef<string | null>(null)
  const poll = useRef<number | null>(null)
  const stop = () => {
    if (poll.current) window.clearTimeout(poll.current)
    poll.current = null
  }
  useEffect(() => stop, [])

  /*
   * THE PUSH FAST-PATH. The BFF now pokes `fork_progress` on every stage
   * transition (edge_event lane), so the spinner tracks the fork in real time
   * and the net below is demoted to what a net should be — the recovery for a
   * frame the socket lost. One re-ask per event, scoped by fork id.
   */
  useEffect(() => {
    return onEdgeEvent((event, data) => {
      if (event !== 'fork_progress') return
      const id = forkId.current
      if (!id || data['fork_id'] !== id) return
      forkAcrossProgress(id).then((p) => setProgress(p)).catch(() => {})
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Ask where it has got to, on avg-latency's cadence. Display only. */
  const watch = (id: string) => {
    const started = Date.now()
    let slow = 0
    const tick = () => {
      const elapsed = Date.now() - started
      const gap = forkPollDelay(elapsed, slow)
      if (elapsed >= 5_000) slow++
      poll.current = window.setTimeout(() => {
        forkAcrossProgress(id)
          .then((p) => setProgress(p))
          // A progress request that fails says nothing about the fork — the POST is
          // the one that decides. Keep asking rather than reporting weather as failure.
          .catch(() => {})
          .finally(() => {
            if (poll.current !== null) tick()
          })
      }, gap)
    }
    poll.current = window.setTimeout(tick, 0)
  }

  const submit = async () => {
    const nm = name.trim()
    const dir = cwd.trim()
    if (!nm || !dir) return
    setBusy(true)
    setFailure(null)
    try {
      if (!across) {
        // The machine is NAMED, never inherited from the ambient relay target —
        // "this machine" means the SOURCE agent's machine, and the relay may be
        // pointed elsewhere (owner, 2026-08-25: every local fork landed on a
        // machine without the chosen folder's home → cwd_unwritable).
        const r = await forkAgent(agent.session, {
          newName: nm,
          cwd: dir,
          host: agent.host,
          device: sourceDeviceId,
        })
        if (r.error) {
          setFailure(forkFailure(r.error, r.message ?? r.detail, { target: targetName }))
          return
        }
        onForked(r.session ?? nm, agent.host)
        return
      }
      forkId.current ??= crypto.randomUUID()
      const id = forkId.current
      setProgress(undefined)
      watch(id)
      const r = await forkAcross({
        forkId: id,
        sourceDeviceId,
        sourceSession: agent.session,
        targetDeviceId: target,
        cwd: dir,
        newName: nm,
      })
      if (r.error) {
        setFailure(forkFailure(r.error, r.message ?? r.detail, { target: targetName }))
        return
      }
      setDone({ session: r.target?.session ?? nm, machine: targetName })
    } catch (e) {
      /*
       * A relayed refusal arrives THROWN, not returned — `api.post` raises
       * ApiError for any non-2xx, so the `r.error` branch above never sees the
       * local fork's codes. This catch used to treat every throw as "the browser
       * gave up", and the owner met a plain `cwd_unwritable` dressed as "Lost
       * contact with the cloud" (2026-08-25) — a named refusal from a machine,
       * shown as a transport mystery. Unwrap the code and let the table speak.
       */
      if (e instanceof ApiError) {
        const body = (e.body ?? {}) as { error?: unknown; message?: unknown; detail?: unknown }
        const code = typeof body.error === 'string' && body.error ? body.error : e.message
        const said = [body.message, body.detail].find((v): v is string => typeof v === 'string')
        setFailure(forkFailure(code, said ?? null, { target: targetName }))
        return
      }
      // A thrown non-ApiError is the browser's, not the endpoint's — no code to look up.
      setFailure(forkFailure(null, String(e instanceof Error ? e.message : e), { target: targetName }))
    } finally {
      stop()
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="sm-fork">
        <p className="ff-done">
          Created <b>{done.session}</b> on {done.machine}.
        </p>
        {/*
          * NOT a redirect, and not a wait for the roster. A forked agent is alive on
          * its machine before it has been announced to the cloud, so "it is not in the
          * list yet" means the list has not heard — never that the fork failed. A row
          * inserted optimistically here would become an entry that opens nothing if the
          * import did fail; a button the person presses when they are ready cannot.
          */}
        <p className="sm-hint">
          It may take a few seconds to appear in the list — that is the list catching up,
          not a problem with the fork.
        </p>
        <div className="sm-actions">
          <button type="button" className="sheet-btn" onClick={onBack}>
            Done
          </button>
          <button
            type="button"
            className="sheet-btn primary"
            onClick={() => onForked(done.session, null)}
          >
            Open it
          </button>
        </div>
      </div>
    )
  }

  const legs = forkLegViews(progress, { source: machine, target: targetName })

  return (
    <div className="sm-fork">
      <label className="sm-field">
        <span className="sm-label">New session name</span>
        <input
          className="sm-input"
          value={name}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <div className="sm-field">
        <span className="sm-label">Machine</span>
        {/*
          * Machines that cannot be used are LISTED and disabled, with the reason on the
          * row. Hidden, they read as machines you never paired, and the reader goes
          * looking for a pairing problem that does not exist.
          */}
        <select
          className="sm-input ff-machine"
          aria-label="Machine"
          value={target}
          disabled={busy}
          onChange={(e) => {
            setTarget(e.target.value)
            /*
             * The folder belongs to the machine it is on; carrying this one's path to
             * another machine would suggest a directory that may not exist there. Empty
             * hands the question back to FolderField, which asks the NEW machine for its
             * home — the same answer create would have given for that machine.
             */
            if (e.target.value !== target) setCwd('')
          }}
        >
          <option value="">{machine} (this machine)</option>
          {targets.map((t) => (
            <option key={t.deviceId} value={t.deviceId} disabled={!t.selectable}>
              {t.selectable ? t.name : `${t.name} — ${t.why}`}
            </option>
          ))}
        </select>
      </div>

      <div className="sm-field">
        <span className="sm-label">Folder path</span>
        <FolderField
          deviceId={across ? target : sourceDeviceId}
          serverName={targetName}
          value={cwd}
          onChange={(p) => setCwd(p)}
        />
      </div>

      <p className="sm-hint">
        {across
          ? `The conversation is copied to ${targetName} as a new session; this one keeps going.`
          : 'The conversation is copied here as a new session; this one keeps going.'}
      </p>

      {busy && across && (
        <ol className="ff-legs" aria-label="Fork progress">
          {legs.map((l) => (
            <li key={l.leg} className={`ff-leg ${l.state}`}>
              <span className="ff-leg-label">{l.label}</span>
              {/* The seconds are the point: they are what tells a long wait from a
                  stuck one, which a spinner cannot. */}
              {l.seconds !== null && <span className="ff-leg-s">{Math.round(l.seconds)}s</span>}
            </li>
          ))}
          <li className="ff-leg-stage">{forkStageLabel(progress?.stage, { source: machine, target: targetName })}</li>
        </ol>
      )}

      {failure && (
        <div className="ff-fail" role="alert">
          <p className="ff-fail-title">{failure.title}</p>
          {failure.detail && <p className="ff-fail-detail">{failure.detail}</p>}
        </div>
      )}

      <div className="sm-actions">
        <button type="button" className="sheet-btn" disabled={busy} onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="sheet-btn primary"
          disabled={busy || !name.trim() || !cwd.trim() || (across && !targetRow?.selectable)}
          onClick={() => void submit()}
        >
          {busy ? 'Forking…' : failure?.retry ? 'Try again' : 'Fork'}
        </button>
      </div>
    </div>
  )
}
